import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { forceFullLoad, isUnsavedFormOpen, useIsStale } from './appVersion';
const PRIMARY_BUTTON = 0;
/** The same-origin link a plain click would follow to a path other than `currentPathname`, if any. */
function findLinkToAnotherPath(event, currentPathname) {
    if (event.button !== PRIMARY_BUTTON || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return null;
    }
    const anchor = event.target instanceof Element ? event.target.closest('a') : null;
    if (!(anchor instanceof HTMLAnchorElement) || !anchor.hasAttribute('href') || anchor.hasAttribute('download')) {
        return null;
    }
    const target = anchor.getAttribute('target');
    if (target && target !== '_self') {
        return null;
    }
    const url = new URL(anchor.href);
    return url.origin === window.location.origin && url.pathname !== currentPathname ? anchor : null;
}
/** While the tab is stale, turns every change to another path into a full page load; mount once inside the router. */
export function useFullLoadWhenStale() {
    const stale = useIsStale();
    const { pathname } = useLocation();
    const previousPathname = useRef(pathname);
    // Capture phase on the document runs before the router's link handler, so an intercepted click loads the target
    // fresh instead of rendering it with the old code first.
    useEffect(() => {
        if (!stale) {
            return undefined;
        }
        const handleClick = (event) => {
            const anchor = findLinkToAnotherPath(event, pathname);
            // An open unsaved form's guard must ask first; the path-change effect below follows up.
            if (!anchor || isUnsavedFormOpen()) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            forceFullLoad('navigation', anchor.href);
        };
        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, [stale, pathname]);
    // Covers navigations in code, back/forward, and links followed after the unsaved-changes guard let them through.
    useEffect(() => {
        if (previousPathname.current === pathname) {
            return;
        }
        previousPathname.current = pathname;
        if (stale) {
            forceFullLoad('navigation');
        }
    }, [stale, pathname]);
}
